import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Percent,
  ShieldAlert,
  Receipt,
  ArrowRight,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  Info,
  CalendarClock,
} from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import DateField from "../components/ui/DateField";
import { api, fmtINR, fmtPct, formatApiError } from "../lib/api";

const BAND_COLORS = {
  LT: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  ST: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  SLAB: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
};

const BASIS_LABEL = {
  maturity: "On maturity",
  long_term_default: "Min. long-term date",
  sale_date: "Sold on date",
  today: "Today",
};

// New-regime (FY 2025-26) marginal slab from taxable income (₹).
function marginalFromIncome(income) {
  const i = Number(income) || 0;
  if (i <= 400000) return 0;
  if (i <= 800000) return 5;
  if (i <= 1200000) return 10;
  if (i <= 1600000) return 15;
  if (i <= 2000000) return 20;
  if (i <= 2400000) return 25;
  return 30;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Taxation() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [events, setEvents] = useState([]);

  // valuation controls
  const [mode, setMode] = useState("maturity"); // maturity | date | today
  const [saleDate, setSaleDate] = useState(todayISO());

  // tax slab
  const [marginalRate, setMarginalRate] = useState("");
  const [income, setIncome] = useState("");

  // sale simulator
  const [saleForm, setSaleForm] = useState({
    asset_id: "",
    units: "",
    sale_price_per_unit: "",
    sale_date: todayISO(),
  });
  const [saleResult, setSaleResult] = useState(null);

  const valuationParam = useCallback(() => {
    if (mode === "date") return `?valuation_date=${saleDate}`;
    if (mode === "today") return `?valuation_date=${todayISO()}`;
    return ""; // maturity → per-asset default
  }, [mode, saleDate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: tax }, { data: ev }, prof] = await Promise.all([
        api.get(`/tax/portfolio${valuationParam()}`),
        api.get("/tax/events"),
        api.get("/profile/financial").catch(() => ({ data: {} })),
      ]);
      setSummary(tax);
      setEvents(ev.events || []);
      if (tax.marginal_tax_rate != null)
        setMarginalRate(String(tax.marginal_tax_rate));
      else if (prof?.data?.marginal_tax_rate != null)
        setMarginalRate(String(prof.data.marginal_tax_rate));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [valuationParam]);

  useEffect(() => {
    load();
  }, [load]);

  const saveSlab = async (rate) => {
    try {
      await api.put("/profile/financial", {
        marginal_tax_rate: rate === "" ? null : Number(rate),
      });
      toast.success("Tax slab updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const simulateSale = async () => {
    if (!saleForm.asset_id) return toast.error("Select an asset");
    try {
      const { data } = await api.post("/tax/simulate-sale", {
        asset_id: saleForm.asset_id,
        units: saleForm.units ? parseFloat(saleForm.units) : null,
        sale_price_per_unit: saleForm.sale_price_per_unit
          ? parseFloat(saleForm.sale_price_per_unit)
          : null,
        sale_date: saleForm.sale_date || null,
      });
      setSaleResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-8" data-testid="taxation-page">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-slate-500">
            Capital gains
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
            Taxation
          </h1>
          <p
            className="text-sm text-slate-500 mt-1 flex items-center gap-2"
            data-testid="tax-disclaimer"
          >
            <Info className="w-4 h-4" />
            Approximate educational estimate — not tax advice. Indian rules,
            editable in{" "}
            <Link
              to="/dashboard/assumptions"
              className="text-emerald-600 hover:underline"
            >
              Assumptions → Taxation
            </Link>
            .
          </p>
        </div>

        {/* ---------------- Controls ---------------- */}
        <div className="card-soft p-4 mb-6 grid gap-4 lg:grid-cols-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
              <CalendarClock className="h-3.5 w-3.5" /> Value the tax as of
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-lg bg-slate-100 dark:bg-slate-800 p-0.5">
                {[
                  ["maturity", "On maturity"],
                  ["date", "If sold on date"],
                  ["today", "Today"],
                ].map(([m, label]) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                      mode === m
                        ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm"
                        : "text-slate-500"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {mode === "date" && (
                <DateField
                  value={saleDate}
                  onChange={(v) => setSaleDate(v)}
                  className="!w-auto"
                />
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              "On maturity" uses each asset's maturity date — or, when none, the
              earliest date long-term gains apply (so gains aren't taxed as
              short-term by default).
            </p>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
              <Percent className="h-3.5 w-3.5" /> Your marginal tax slab
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={marginalRate}
                onChange={(e) => {
                  setMarginalRate(e.target.value);
                  saveSlab(e.target.value);
                }}
                className="input-soft !py-1.5 !w-auto"
              >
                <option value="">Default (30%)</option>
                {[0, 5, 10, 15, 20, 25, 30].map((r) => (
                  <option key={r} value={r}>
                    {r}%
                  </option>
                ))}
              </select>
              <span className="text-xs text-slate-400">or from income</span>
              <input
                type="number"
                placeholder="Annual income ₹"
                value={income}
                onChange={(e) => setIncome(e.target.value)}
                className="input-soft !py-1.5 !w-40"
              />
              <button
                onClick={() => {
                  // Only derive when a real income is entered — otherwise an
                  // empty field would silently apply the 0% slab.
                  if (income === "" || Number(income) <= 0) {
                    toast.error("Enter your annual income first");
                    return;
                  }
                  const r = marginalFromIncome(income);
                  setMarginalRate(String(r));
                  saveSlab(r);
                }}
                disabled={income === "" || Number(income) <= 0}
                className="btn-secondary !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Apply
              </button>
              {income !== "" && Number(income) > 0 && (
                <span className="text-xs text-slate-500">
                  → <b>{marginalFromIncome(income)}%</b> slab
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Pick your slab directly, or enter your annual income and press
              Apply to derive it. Used for slab-taxed gains (debt, gold, etc.)
              on the new-regime FY 2025-26 slabs.
            </p>
          </div>
        </div>

        {loading && <div className="text-slate-500">Loading…</div>}

        {summary && (
          <>
            {/* Headline tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Tile
                label={mode === "today" ? "Value Today" : "Value at Valuation"}
                value={fmtINR(summary.total_current_value, { compact: true })}
                icon={TrendingUp}
                colour="emerald"
              />
              <Tile
                label="Total Cost Basis"
                value={fmtINR(summary.total_cost_basis, { compact: true })}
                icon={Receipt}
                colour="slate"
              />
              <Tile
                label="Estimated Tax"
                value={fmtINR(summary.total_embedded_tax_liability, {
                  compact: true,
                })}
                icon={ShieldAlert}
                colour="amber"
              />
              <Tile
                label="Net of Tax"
                value={fmtINR(summary.net_of_tax_total, { compact: true })}
                icon={Percent}
                colour="indigo"
              />
            </div>

            {/* Per-asset breakdown */}
            <div className="card-soft overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Per-Asset Tax Position
                </h2>
                {summary.approximate_any && (
                  <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                    Some assets use approximate cost basis
                  </span>
                )}
              </div>
              <div className="divide-y divide-slate-200 dark:divide-slate-800">
                {(summary.assets || []).map((a) => (
                  <div key={a.asset_id} data-testid={`tax-asset-${a.asset_id}`}>
                    <button
                      onClick={() =>
                        setExpanded((s) => ({
                          ...s,
                          [a.asset_id]: !s[a.asset_id],
                        }))
                      }
                      className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
                    >
                      <div className="flex items-center gap-3 text-left">
                        {expanded[a.asset_id] ? (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        )}
                        <div>
                          <div className="font-medium text-slate-900 dark:text-white">
                            {a.asset_name}
                          </div>
                          <div className="text-xs text-slate-500 flex items-center gap-2">
                            <span>{a.asset_class}</span>
                            <span className="text-slate-300">·</span>
                            <span className="inline-flex items-center gap-1 text-sky-600">
                              <CalendarClock className="h-3 w-3" />
                              {BASIS_LABEL[a.valuation_basis] ||
                                a.valuation_basis}
                              {a.valuation_date ? ` (${a.valuation_date})` : ""}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-right">
                        <div className="hidden md:block">
                          <div className="text-xs text-slate-500">Value</div>
                          <div className="font-medium">
                            {fmtINR(a.current_value, { compact: true })}
                          </div>
                        </div>
                        <div className="hidden md:block">
                          <div className="text-xs text-slate-500">Gain</div>
                          <div
                            className={`font-medium ${a.embedded_gain >= 0 ? "text-emerald-600" : "text-rose-500"}`}
                          >
                            {fmtINR(a.embedded_gain, { compact: true })}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500">Tax</div>
                          <div className="font-medium text-amber-600">
                            {fmtINR(a.embedded_tax_liability, {
                              compact: true,
                            })}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500">
                            Net of tax
                          </div>
                          <div className="font-medium">
                            {fmtINR(a.net_of_tax_value, { compact: true })}
                          </div>
                        </div>
                      </div>
                    </button>
                    {expanded[a.asset_id] && (
                      <div className="bg-slate-50 dark:bg-slate-900/50 px-6 py-4">
                        <div className="flex flex-wrap gap-4 text-xs text-slate-500 mb-3">
                          <span>
                            Value today:{" "}
                            <b className="text-slate-700 dark:text-slate-300">
                              {fmtINR(a.current_value_today)}
                            </b>
                          </span>
                          <span>
                            Value at {a.valuation_date}:{" "}
                            <b className="text-slate-700 dark:text-slate-300">
                              {fmtINR(a.projected_value)}
                            </b>
                          </span>
                        </div>
                        <div className="text-xs font-semibold text-slate-500 uppercase mb-2">
                          Tax Lots ({a.lots.length})
                        </div>
                        <table
                          className="w-full text-sm"
                          data-testid={`lots-table-${a.asset_id}`}
                        >
                          <thead className="text-xs text-slate-500">
                            <tr>
                              <th className="text-left py-1">Acquired</th>
                              <th className="text-right">Cost</th>
                              <th className="text-right">Market</th>
                              <th className="text-right">Gain</th>
                              <th className="text-right">Type</th>
                              <th className="text-right">Rate</th>
                              <th className="text-right">Tax</th>
                            </tr>
                          </thead>
                          <tbody>
                            {a.lots.map((l, i) => (
                              <tr
                                key={i}
                                className="border-t border-slate-200 dark:border-slate-700"
                              >
                                <td className="py-2">
                                  {l.acquisition_date}{" "}
                                  {l.is_approximate && (
                                    <span className="text-amber-600 text-xs">
                                      approx
                                    </span>
                                  )}
                                </td>
                                <td className="text-right">
                                  {fmtINR(l.cost_basis, { compact: true })}
                                </td>
                                <td className="text-right">
                                  {fmtINR(l.market_value, { compact: true })}
                                </td>
                                <td
                                  className={`text-right ${l.gain >= 0 ? "text-emerald-600" : "text-rose-500"}`}
                                >
                                  {fmtINR(l.gain, { compact: true })}
                                </td>
                                <td className="text-right">
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full ${BAND_COLORS[l.classification] || "bg-slate-100 text-slate-700"}`}
                                  >
                                    {l.classification}
                                  </span>
                                </td>
                                <td className="text-right">
                                  {fmtPct(l.rate_applied_pct, 1)}
                                </td>
                                <td className="text-right font-medium text-amber-600">
                                  {fmtINR(l.estimated_tax, { compact: true })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Sale simulator */}
            <div className="card-soft p-6 mb-8" data-testid="sale-simulator">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-emerald-600" /> Simulate a
                specific sale (FIFO, post-tax)
              </h2>
              <p className="text-xs text-slate-500 mb-4">
                Equity ₹1.25L LTCG exemption tracked per FY. Pick a sale date to
                test short- vs long-term treatment.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <select
                  value={saleForm.asset_id}
                  onChange={(e) =>
                    setSaleForm((s) => ({ ...s, asset_id: e.target.value }))
                  }
                  className="input-soft !py-2"
                  data-testid="sale-asset-select"
                >
                  <option value="">Select asset…</option>
                  {(summary.assets || []).map((a) => (
                    <option key={a.asset_id} value={a.asset_id}>
                      {a.asset_name}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="Units (blank = all)"
                  value={saleForm.units}
                  onChange={(e) =>
                    setSaleForm((s) => ({ ...s, units: e.target.value }))
                  }
                  className="input-soft !py-2"
                />
                <input
                  type="number"
                  placeholder="Price per unit"
                  value={saleForm.sale_price_per_unit}
                  onChange={(e) =>
                    setSaleForm((s) => ({
                      ...s,
                      sale_price_per_unit: e.target.value,
                    }))
                  }
                  className="input-soft !py-2"
                />
                <DateField
                  value={saleForm.sale_date}
                  onChange={(v) =>
                    setSaleForm((s) => ({ ...s, sale_date: v }))
                  }
                />
                <button
                  onClick={simulateSale}
                  className="btn-primary"
                  data-testid="sale-simulate-btn"
                >
                  Simulate
                </button>
              </div>
              {saleResult && (
                <div
                  className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3"
                  data-testid="sale-result"
                >
                  <Result
                    label="Realized Gain"
                    value={fmtINR(saleResult.realized_gain, { compact: true })}
                  />
                  <Result
                    label="LT Gain"
                    value={fmtINR(saleResult.lt_gain, { compact: true })}
                  />
                  <Result
                    label="ST Gain"
                    value={fmtINR(saleResult.st_gain, { compact: true })}
                  />
                  <Result
                    label="Tax"
                    value={fmtINR(saleResult.tax, { compact: true })}
                    colour="amber"
                  />
                  <Result
                    label="Sale Proceeds"
                    value={fmtINR(saleResult.sale_proceeds, { compact: true })}
                  />
                  <Result
                    label="Net Proceeds"
                    value={fmtINR(saleResult.net_proceeds, { compact: true })}
                    colour="emerald"
                  />
                  <Result label="FY" value={saleResult.fy} />
                  <Result
                    label="₹1.25L exemption used"
                    value={fmtINR(saleResult.equity_ltcg_exemption_used, {
                      compact: true,
                    })}
                  />
                </div>
              )}
            </div>

            {/* FY events */}
            {events.length > 0 && (
              <div className="card-soft p-6">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  Recorded Tax Events
                </h2>
                <table
                  className="w-full text-sm"
                  data-testid="tax-events-table"
                >
                  <thead className="text-xs text-slate-500">
                    <tr>
                      <th className="text-left py-2">Date</th>
                      <th>FY</th>
                      <th>Class</th>
                      <th className="text-right">Gain</th>
                      <th className="text-right">Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr
                        key={e.id}
                        className="border-t border-slate-200 dark:border-slate-700"
                      >
                        <td className="py-2">{e.event_date}</td>
                        <td>{e.financial_year}</td>
                        <td>{e.asset_class}</td>
                        <td className="text-right">
                          {fmtINR(e.realized_gain, { compact: true })}
                        </td>
                        <td className="text-right text-amber-600">
                          {fmtINR(e.tax_amount, { compact: true })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function Tile({ label, value, icon: Icon, colour = "emerald" }) {
  const colorMap = {
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    indigo: "text-indigo-600",
    slate: "text-slate-600",
  };
  return (
    <div className="card-soft p-5 relative">
      <Icon
        className={`w-5 h-5 absolute top-4 right-4 opacity-50 ${colorMap[colour]}`}
      />
      <div className="text-xs text-slate-500 uppercase tracking-wider">
        {label}
      </div>
      <div className="text-2xl font-bold text-slate-900 dark:text-white mt-2 num-mono">
        {value}
      </div>
    </div>
  );
}

function Result({ label, value, colour }) {
  const c =
    colour === "amber"
      ? "text-amber-600"
      : colour === "emerald"
        ? "text-emerald-600"
        : "text-slate-900 dark:text-white";
  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-lg font-semibold ${c}`}>{value}</div>
    </div>
  );
}
